import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { api } from '../lib/api';
import { useI18n } from '../lib/i18n';
import { formatDateTime, formatMinutes } from '../lib/format';
import { FormGrid, FormPage } from '../components/FormPage';
import { Detail, Field, Input, Textarea, useToast } from '../components/ui';

/**
 * Route: `/attendance/sessions/:id/end` — a manager ends a shift someone forgot
 * to end. A confirm-with-a-reason action, so it is a page like the others. The
 * same page sets the real end of a forgotten shift no Desktop Agent tracked,
 * closed at its rostered end until then (2026-10-21): the end is required there.
 */
export function EndSessionFormPage() {
  const { id = '' } = useParams();
  const { t, locale, localized } = useI18n();
  const toast = useToast();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [endedAt, setEndedAt] = useState('');
  const [note, setNote] = useState('');

  const session = useQuery({
    queryKey: ['attendance', 'session', id],
    queryFn: () => api.get<any>(`/attendance/sessions/${id}`),
  });

  const s = session.data;
  const name = s ? localized(s.user) : '';
  const settingEnd = !!s?.endUnconfirmed;

  const close = useMutation({
    mutationFn: () =>
      api.post(`/attendance/sessions/${id}/close`, {
        note: note.trim(),
        // datetime-local is the browser's own clock; the API wants an instant.
        ...(endedAt ? { endedAt: new Date(endedAt).toISOString() } : {}),
      }),
    onSuccess: () => {
      toast.success(t(settingEnd ? 'attendance.endSet' : 'attendance.shiftEnded'));
      void queryClient.invalidateQueries({ queryKey: ['attendance'] });
      navigate('/attendance?tab=team');
    },
    onError: (e: Error) => toast.error(e.message),
  });

  return (
    <FormPage
      title={
        s
          ? t(settingEnd ? 'attendance.setEndFor' : s.kind === 'FREE' ? 'attendance.endFreeFor' : 'attendance.endFor', { name })
          : t('attendance.endShift')
      }
      subtitle={t(settingEnd ? 'attendance.setEndNote' : 'attendance.endNote')}
      backTo="/attendance?tab=team"
      backLabel={t('attendance.team')}
      submitLabel={t(settingEnd ? 'attendance.setEnd' : 'attendance.endShift')}
      destructive={!settingEnd}
      submitting={close.isPending}
      disabled={!note.trim() || (settingEnd ? !endedAt : !!s?.endedAt)}
      loading={session.isLoading}
      error={session.error}
      onRetry={() => void session.refetch()}
      onSubmit={() => close.mutate()}
      summary={
        s && (
          <dl>
            <Detail label={t('common.employee')}>{name}</Detail>
            <Detail label={t('attendance.started')}>{formatDateTime(s.startedAt, locale)}</Detail>
            <Detail label={t('attendance.worked')}>{formatMinutes(s.summary.workedMinutes, locale)}</Detail>
            <Detail label={t('attendance.breaks')}>
              {s.breaks.length ? `${s.breaks.length} · ${formatMinutes(s.summary.breakMinutes, locale)}` : '—'}
            </Detail>
          </dl>
        )
      }
    >
      <FormGrid>
        <Field
          label={t('attendance.endedAt')}
          hint={t(settingEnd ? 'attendance.setEndAtHint' : 'attendance.endedAtHint')}
          required={settingEnd}
        >
          <Input type="datetime-local" value={endedAt} onChange={(e) => setEndedAt(e.target.value)} />
        </Field>
        <Field label={t(settingEnd ? 'attendance.setEndReason' : 'attendance.endReason')} required className="sm:col-span-2">
          <Textarea value={note} onChange={(e) => setNote(e.target.value)} maxLength={500} autoFocus />
        </Field>
      </FormGrid>
    </FormPage>
  );
}

/**
 * Routes: `/attendance/devices/:id/disable` and `/attendance/devices/:id/remove`
 * — an administrator switches off a computer someone runs the Desktop Agent on,
 * with the reason they are told. A page, like every confirm-with-a-reason.
 */
export function DeviceActionPage({ action }: { action: 'disable' | 'remove' }) {
  const { id = '' } = useParams();
  const { t, locale, localized } = useI18n();
  const toast = useToast();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [reason, setReason] = useState('');

  // The computer, from the team's list: there is no page of its own to read.
  const computer = useQuery({
    queryKey: ['attendance', 'devices', 'all'],
    queryFn: () => api.get<{ items: any[] }>('/attendance/devices', { status: undefined }),
    select: (data) => data.items.find((c) => c.id === id) ?? null,
  });

  const save = useMutation({
    mutationFn: () => api.post(`/attendance/devices/${id}/${action}`, { reason: reason.trim() || undefined }),
    onSuccess: () => {
      toast.success(t(action === 'disable' ? 'devices.disabled' : 'devices.removedToast'));
      void queryClient.invalidateQueries({ queryKey: ['attendance', 'devices'] });
      navigate('/attendance?tab=computers');
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const c = computer.data;
  const name = c?.name ?? '';
  return (
    <FormPage
      title={t(action === 'disable' ? 'devices.disableTitle' : 'devices.removeTitle', { name })}
      subtitle={t(action === 'disable' ? 'devices.disableNote' : 'devices.removeNote', { person: c ? localized(c.user) : '' })}
      backTo="/attendance?tab=computers"
      backLabel={t('attendance.computers')}
      submitLabel={t(action === 'disable' ? 'devices.disable' : 'devices.remove')}
      destructive
      submitting={save.isPending}
      disabled={action === 'disable' ? !reason.trim() : false}
      loading={computer.isLoading}
      error={computer.error ?? (computer.isSuccess && !c ? new Error(t('devices.notFound')) : null)}
      onRetry={() => void computer.refetch()}
      onSubmit={() => save.mutate()}
      summary={
        c && (
          <dl>
            <Detail label={t('common.employee')}>{localized(c.user)}</Detail>
            <Detail label={t('devices.computer')}>{[c.name, c.osName].filter(Boolean).join(' · ')}</Detail>
            <Detail label={t('devices.lastReportCol')}>{c.lastHeartbeatAt ? formatDateTime(c.lastHeartbeatAt, locale) : t('agent.never')}</Detail>
          </dl>
        )
      }
    >
      <FormGrid>
        <Field label={t('devices.reason')} required={action === 'disable'} hint={t('devices.reasonHint')} className="sm:col-span-2">
          <Textarea value={reason} onChange={(e) => setReason(e.target.value)} maxLength={500} autoFocus />
        </Field>
      </FormGrid>
    </FormPage>
  );
}
